/**
 * The run header: the way back to the runs, the target's name, the last
 * action's failure, and the status card — objective, stage and execution
 * state, analytical outcome, and the approved profile version.
 */

import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

import type { AnalysisDay2State, AnalysisRunSummary } from '../../api.js';
import { InlineAlert, Tag } from '../../ui.js';
import { PageTitle } from '../common.js';
import type { AnalysisRunPageCopy } from './run-page-copy.js';
import { card } from './run-page-styles.js';
import { humanState, toneFor } from './run-state-labels.js';

export function RunHeader({
  run,
  day2,
  error,
  copy,
}: {
  readonly run: AnalysisRunSummary;
  readonly day2: AnalysisDay2State;
  readonly error: string | null;
  readonly copy: AnalysisRunPageCopy;
}): ReactNode {
  return (
    <>
      <p style={{ marginBottom: 12 }}>
        <Link to="/analysis" style={{ color: 'var(--text-link)' }}>
          ← {copy.back}
        </Link>
      </p>
      <PageTitle>{run.targetBusinessName}</PageTitle>
      {error ? <InlineAlert tone="error" title={error} role="alert" /> : null}
      <div style={{ ...card, marginBottom: 'var(--space-lg)' }}>
        <p style={{ color: 'var(--text-secondary)' }}>{run.objective}</p>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
          <Tag tone={toneFor(run.executionState)}>
            {humanState(run.currentStage)} · {humanState(run.executionState)}
          </Tag>
          {run.analyticalOutcome ? (
            <Tag tone={toneFor(run.analyticalOutcome)}>
              {humanState(run.analyticalOutcome)}
            </Tag>
          ) : null}
          {day2.approvedProfile ? (
            <Tag tone="success">
              Approved profile v{day2.approvedProfile.profileVersion}
            </Tag>
          ) : null}
        </div>
      </div>
    </>
  );
}
